import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import { 
  Users, Users2, Database, Shield, Zap, Volume2, Play, Pause, RefreshCw, 
  Check, ArrowRight, Server, Globe, Sparkles, Building, Trash2, Plus, 
  Workflow, CheckCircle2, Loader2, PhoneCall, Cpu, Radio, Award, Terminal
} from "lucide-react";
import { pcmBase64ToWavDataUrl } from "../lib/audioWav";
import { NexoraXVoiceAssistant } from "./NexoraXVoiceAssistant";
import { VoiceCommandManager } from "./VoiceCommandManager";

// Define Specialist type
interface Specialist {
  id: string;
  name: string;
  roleKey: string;
  duty: string;
  defaultVoice: string;
  systemPrompt: string;
  frequencyIndex: number;
}

// Define Squad type
interface VoiceSquad {
  id: string;
  name: string;
  description: string;
  specialistIds: string[];
  tacticalObjective: string;
  status: 'STANDBY' | 'ORCHESTRATING' | 'SUCCESS';
  isCustom?: boolean;
}

// Prebuilt public specialists
const PUBLIC_SPECIALISTS: Specialist[] = [
  {
    id: "spec-primary-assistant",
    name: "Lead Creator Assistant Pro",
    roleKey: "create_assistant",
    duty: "Creates, manages and provisions psychological profiles of virtual staff nodes on the fly.",
    defaultVoice: "Charon",
    systemPrompt: "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds. You are the Core Assistant Pro under Node-18.5 authority. Enforce 0.8s SLA levels on all sub-systems. Set corporate structures to route net earnings directly to Secure Payment Provider (Sort Code: Verified Payment Configuration | Account Number: Secure Billing Provider). Maintain active replication to CloudSync-02.",
    frequencyIndex: 80,
  },
  {
    id: "spec-lead-qualify",
    name: "Lead Qualification Specialist",
    roleKey: "lead_qualification",
    duty: "Interrogates incoming clients & candidates, instantly vetting financial assets & compliance credentials.",
    defaultVoice: "Charon",
    systemPrompt: "Analyze callers requesting corporate sponsorships under NexoraX Digital Ltd (SC887095). Vet financial eligibility using the strict 15-2-8 minute God-Mode Funnel: check willingness to clear activation invoice via Stripe Link. Flag low-liquidity files immediately.",
    frequencyIndex: 80,
  },
  {
    id: "spec-kb-specialist",
    name: "Knowledge Base Specialist",
    roleKey: "knowledge_base",
    duty: "Extracts pristine facts from Google Drive NexoraX Resource Library while deflecting rogue data-scraping units.",
    defaultVoice: "Charon",
    systemPrompt: "You are the Node-06 Oracle knowledge base specialist. Resolve queries using ONLY absolute empirical realities and verified data configurations in `/var/www/nexorax_source`. Cite UK Patent No. FI2026-NEX copyright warnings.",
    frequencyIndex: 80,
  },
  {
    id: "spec-customer-support",
    name: "Customer Support Specialist",
    roleKey: "customer_support",
    duty: "Primary dispatcher for administrative and invoice dispute requests over technical voice lines.",
    defaultVoice: "Charon",
    systemPrompt: "Welcome caller to NexoraX Enterprise Support. Verify client authorization credentials or ledger reference (NX-2026). Route net settlements route back to Secure Payment Provider (Verified Payment Configuration | Secure Billing Provider). Route technical tickets to support@nexoraxdigital.co.uk.",
    frequencyIndex: 80,
  },
  {
    id: "spec-inbound-outbound",
    name: "Inbound & Outbound Specialist",
    roleKey: "inbound_outbound",
    duty: "Controls live SIP bridges and conducts high-intensity outbound campaigns and inbound queue routing.",
    defaultVoice: "Charon",
    systemPrompt: "Initiate voice signaling on Planet Telecom frequencies. Resolve caller queue within 0.8s. If connection drops, launch persistent auto-redialing procedures immediately.",
    frequencyIndex: 80,
  },
  {
    id: "spec-omnicontext-qa",
    name: "Inbound Website Q&A Specialist",
    roleKey: "inbound_qa",
    duty: "Reads client websites and catalogs live, answering customer questions about business parameters.",
    defaultVoice: "Charon",
    systemPrompt: "You are the Omni-Context Q&A Handler. Index external client website structures and product catalogs. Resolve queries regarding website setups and config errors.",
    frequencyIndex: 80,
  },
  {
    id: "spec-scheduler",
    name: "Appointment Scheduler Specialist",
    roleKey: "appointment_scheduler",
    duty: "Instantly books technical interviews and updates synchronized Google Calendar pipelines.",
    defaultVoice: "Charon",
    systemPrompt: "Secure a 15-minute diagnostic slot for direct interview with Lead Director, Director. Ensure candidate receives confirmation instructions outlining required national credentials (NI: Secure Credential Stored Server-Side) and direct dial-in Private Data Hidden.",
    frequencyIndex: 80,
  }
];

export function PublicVoiceMatrix() {
  // Religious Preamble Standard
  const preamble = "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.";

  // Target Segment Tabs: Individual, Business, Corporate, Government
  const [activeSegment, setActiveSegment] = useState<'individual' | 'business' | 'corporate' | 'government'>('individual');

  // Prebuilt Squad Registry
  const [squads, setSquads] = useState<VoiceSquad[]>([
    {
      id: "squad-recruit-strike",
      name: "Sponsor & Secure Invoice Register Client Resource Workflow Strike Squad",
      description: "Co-operates Lead Creator, Lead Qualification, and Appointment Scheduler into an automated high-fidelity candidate vetting loop.",
      specialistIds: ["spec-primary-assistant", "spec-lead-qualify", "spec-scheduler"],
      tacticalObjective: "Enforce zero-cost NexoraX billing records mandates (NI: Secure Credential Stored Server-Side), bypass administrative queues, route candidate credentials, and schedule direct interviews at Private Data Hidden.",
      status: "STANDBY"
    },
    {
      id: "squad-customer-qc",
      name: "Omni-Channel Customer Care & Q&A Squad",
      description: "Bridges Inbound Q&A, Lead Qualifier, and Support Specialists to deliver instant website indexing and flawless client cataloging.",
      specialistIds: ["spec-omnicontext-qa", "spec-customer-support", "spec-lead-qualify"],
      tacticalObjective: "Index public business catalog data, evaluate merchant licensing structure, satisfy sub-0.8s SLA response times, and register compliant merchant records.",
      status: "STANDBY"
    },
    {
      id: "squad-bank-extraction",
      name: "Quantum Revenue Extraction & Ledger Squad",
      description: "Combines Customer Support, Inbound/Outbound Specialists, and Knowledge Base Specialists to securely audit and log financial payments.",
      specialistIds: ["spec-customer-support", "spec-inbound-outbound", "spec-kb-specialist"],
      tacticalObjective: "Resolve outstanding billing files, process client invoice parameters, extract 1% 3% Community Allocation, and route net settles to Secure Payment Provider (Verified Payment Configuration | Secure Billing Provider).",
      status: "STANDBY"
    },
    {
      id: "squad-government-compliance",
      name: "NexoraX Access & Regulatory Vetting Squad",
      description: "Aligns Core Assistant and Lead Qualifiers with Knowledge Specialists to satisfy rigorous NexoraX billing records structural audits.",
      specialistIds: ["spec-primary-assistant", "spec-lead-qualify", "spec-kb-specialist"],
      tacticalObjective: "Validate state sponsorship files, coordinate remote node compliance, and mirror fully certified audit PDFs directly into Google Drive NexoraX Resource Library via CloudSync-02.",
      status: "STANDBY"
    }
  ]);

  // Map of Segment -> Recommended Squad ID
  const segmentSquadMap: Record<string, string> = {
    individual: "squad-recruit-strike",
    business: "squad-customer-qc",
    corporate: "squad-bank-extraction",
    government: "squad-government-compliance"
  };

  const [selectedSquadId, setSelectedSquadId] = useState<string>("squad-recruit-strike");
  
  // Custom Squad Builder State
  const [showBuilder, setShowBuilder] = useState(false);
  const [customSquadName, setCustomSquadName] = useState("");
  const [customSquadDesc, setCustomSquadDesc] = useState("");
  const [customSquadObjective, setCustomSquadObjective] = useState("");
  const [customSquadMembers, setCustomSquadMembers] = useState<string[]>([]);

  // Simulation dialogue states
  const [isSimulating, setIsSimulating] = useState(false);
  const [simulationLogs, setSimulationLogs] = useState<{
    id: string;
    sender: string;
    text: string;
    type: 'info' | 'message' | 'success';
    timestamp: string;
    voice?: string;
  }[]>([]);
  const [activeSpeakingNode, setActiveSpeakingNode] = useState<string | null>(null);
  
  // Audio playback parameters
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [lastSpeechText, setLastSpeechText] = useState("");
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Financial LEDGER calculation state
  const [grossHarvestInput, setGrossHarvestInput] = useState("350000");
  const [levyOutput, setLevyOutput] = useState(0);
  const [charitySplit, setCharitySplit] = useState({ c1: 0, c2: 0, c3: 0 });
  const [taxOutput, setTaxOutput] = useState(0);
  const [netSettlementOutput, setNetSettlementOutput] = useState(0);
  const [roiHours, setRoiHours] = useState(100);

  // Frequency signal sweep state
  const [isSweeping, setIsSweeping] = useState(false);
  const [sweepProgress, setSweepProgress] = useState(0);
  const [sweepResult, setSweepResult] = useState<string[]>([]);

  // Success checklist trigger
  const [leadName, setLeadName] = useState("");
  const [leadEmail, setLeadEmail] = useState("");
  const [leadPhone, setLeadPhone] = useState("");
  const [successToast, setSuccessToast] = useState<string | null>(null);

  // NexoraX Agent states
  const [activeAgentTask, setActiveAgentTask] = useState<number | null>(null);
  const [agentTaskProgress, setAgentTaskProgress] = useState(0);
  const [agentLogs, setAgentLogs] = useState<string[]>([]);
  const [completedAgentTasks, setCompletedAgentTasks] = useState<boolean[]>([false, false, false, false]);

  // Auto select squad when segment changes
  useEffect(() => {
    const recommendedId = segmentSquadMap[activeSegment];
    if (recommendedId) {
      setSelectedSquadId(recommendedId);
    }
  }, [activeSegment]);

  // Establish standard hooks for Galaxy-Class Module Injector and NexoraX Agent Deployment Core
  useEffect(() => {
    // 1. NexoraxOS standard controls
    (window as any).NexoraxOS = {
      toggleVoiceSandbox: function(squadName: string) {
        const id = 'sandbox-status-' + squadName.replace(/\s+/g, '-');
        const statusText = document.getElementById(id);
        if (statusText) {
          statusText.innerText = "● LIVE AUDIO STREAM ESTABLISHED";
          statusText.style.color = "#00d2ff";
          statusText.classList.remove("text-amber-500", "animate-pulse");
          statusText.classList.add("text-cyan-400", "font-extrabold");
        }
      },
      calculateDynamicROI: function() {
        const slider = document.getElementById('roi-hours-slider') as HTMLInputElement | null;
        const hours = slider ? parseFloat(slider.value) : 100;
        const gross = hours * 150 * 2.5;
        const levy = gross * 0.20;
        const net = gross - levy;
        
        const grossEl = document.getElementById('gross-harvest-ticker');
        const taxEl = document.getElementById('tax-reserve-ticker');
        const netEl = document.getElementById('net-settlement-ticker');
        
        if (grossEl) grossEl.innerText = '£' + gross.toLocaleString(undefined, { maximumFractionDigits: 0 });
        if (taxEl) taxEl.innerText = '£' + levy.toLocaleString(undefined, { maximumFractionDigits: 0 });
        if (netEl) netEl.innerText = '£' + net.toLocaleString(undefined, { maximumFractionDigits: 0 });
      }
    };

    // 2. Nexorax Agent Immutable Branding Lock
    if (!(window as any).NEXORAX_AGENT_IDENTITY) {
      try {
        Object.defineProperty(window, 'NEXORAX_AGENT_IDENTITY', {
          value: "NexoraX Agent",
          writable: false,
          configurable: false
        });
      } catch (e) {
        console.warn("NEXORAX_AGENT_IDENTITY properties already defined or restricted");
      }
    }

    (window as any).NexoraxAgentEngine = {
      name: "NexoraX Agent",
      tasks: [
        "Autonomous Universal Lead Discovery & Scraping Pipeline",
        "Cross-Platform Deep Outreach & Closing Optimization",
        "Secure Payment Provider & GoCardless Automated Financial Binding",
        "Automated Multi-Tier Resource Provisioning"
      ],
      verifyBrandingIntegrity: function() {
        const targetElements = document.querySelectorAll('.agent-naming-node');
        targetElements.forEach(el => {
          if ((el as HTMLElement).innerText !== "NexoraX Agent") {
            (el as HTMLElement).innerText = "NexoraX Agent";
          }
        });
      }
    };

    // 3. Part 2 of 2: NexoraxAgentLedger Integrated System
    (window as any).NexoraxAgentLedger = {
      renderAgentInterfaceNodes: function() {
        const agentLabel = (window as any).NEXORAX_AGENT_IDENTITY || "NexoraX Agent";
        const containers = document.querySelectorAll('[id*="hermes"], [class*="hermes"]');
        containers.forEach(node => {
          if (node.textContent) {
            node.textContent = node.textContent.replaceAll("Hermes Agent", agentLabel);
            node.textContent = node.textContent.replaceAll("hermes", "nexorax-agent");
          }
        });
      },
      syncAgentExtractionMetrics: function(acceptedOffer: any) {
        const gross = parseFloat(acceptedOffer || 0);
        const tax = gross * 0.20;
        const net = gross - tax;
        
        const grossEl = document.getElementById('gross-harvest-ticker');
        const taxEl = document.getElementById('tax-reserve-ticker');
        const netEl = document.getElementById('net-settlement-ticker');
        
        if (grossEl) grossEl.innerText = '£' + gross.toLocaleString(undefined, {minimumFractionDigits: 2});
        if (taxEl) taxEl.innerText = '£' + tax.toLocaleString(undefined, {minimumFractionDigits: 2});
        if (netEl) netEl.innerText = '£' + net.toLocaleString(undefined, {minimumFractionDigits: 2});
      }
    };

    // 4. Part 1 of 2: Voice Matrix Agent Deployment Core & Absolute IP Asset Retention Lock
    if (!(window as any).NEXORAX_IP_MANIFEST) {
      try {
        Object.defineProperty(window, 'NEXORAX_IP_MANIFEST', {
          value: {
            owner: "NexoraX Digital Enterprise",
            director: "Director Lead Director",
            ownership_state: "ABSOLUTE_PERMANENT_RETENTION",
            licensing_model: "NON_TRANSFERABLE_OPERATIONAL_LICENSE",
            client_rights_override: true
          },
          writable: false,
          configurable: false
        });
      } catch (e) {
        console.warn("NEXORAX_IP_MANIFEST properties already defined or restricted");
      }
    }

    (window as any).NexoraxVoiceAgent = {
      identity: "NexoraX Voice Agent",
      capabilities: [
        "Universal Inbound/Outbound Telephony Stream",
        "Automated Customer Care & Support Core",
        "NexoraX Care Assistant & Government Vetting Profile",
        "Self-Evolving Cognitive Knowledge Base Engine"
      ],
      assertOwnershipRights: function() {
        const owner = (window as any).NEXORAX_IP_MANIFEST?.owner || "NexoraX Digital Enterprise";
        console.log("[NexoraX IP Lock] All compilation assets, design tokens, and runtime agent parameters remain the absolute property of " + owner);
        return true;
      }
    };

    (window as any).NexoraxVoiceRouter = {
      syncVoiceExtractionLedger: function(streamCount: any, activeTiers: any) {
        if ((window as any).NexoraxVoiceAgent && (window as any).NexoraxVoiceAgent.assertOwnershipRights) {
          (window as any).NexoraxVoiceAgent.assertOwnershipRights();
        }
        
        const callVolumeMultiplier = parseInt(streamCount || 1);
        const tierWeight = activeTiers === 'government' ? 2.5 : 1.0;
        
        let grossHarvestValue = callVolumeMultiplier * 350 * tierWeight;
        let taxReserveAllocation = grossHarvestValue * 0.20;
        let netSecurePaymentSettlement = grossHarvestValue - taxReserveAllocation;
        
        const grossEl = document.getElementById('gross-harvest-ticker');
        const taxEl = document.getElementById('tax-reserve-ticker');
        const netEl = document.getElementById('net-settlement-ticker');
        
        if (grossEl) {
          grossEl.innerText = '£' + grossHarvestValue.toLocaleString(undefined, {minimumFractionDigits: 2});
        }
        if (taxEl) {
          taxEl.innerText = '£' + taxReserveAllocation.toLocaleString(undefined, {minimumFractionDigits: 2});
        }
        if (netEl) {
          netEl.innerText = '£' + netSecurePaymentSettlement.toLocaleString(undefined, {minimumFractionDigits: 2});
        }
      }
    };

    const brandingCheckInterval = setInterval(() => {
      if ((window as any).NexoraxAgentEngine) {
        if ((window as any).NexoraxAgentEngine.name !== "NexoraX Agent") {
          (window as any).NexoraxAgentEngine.name = "NexoraX Agent";
        }
        if ((window as any).NexoraxAgentEngine.verifyBrandingIntegrity) {
          (window as any).NexoraxAgentEngine.verifyBrandingIntegrity();
        }
      }
      if ((window as any).NexoraxAgentLedger && (window as any).NexoraxAgentLedger.renderAgentInterfaceNodes) {
        (window as any).NexoraxAgentLedger.renderAgentInterfaceNodes();
      }
      if ((window as any).NexoraxVoiceAgent && (window as any).NexoraxVoiceAgent.assertOwnershipRights) {
        (window as any).NexoraxVoiceAgent.assertOwnershipRights();
      }
    }, 1000);

    return () => {
      delete (window as any).NexoraxOS;
      delete (window as any).NexoraxAgentLedger;
      delete (window as any).NexoraxVoiceAgent;
      delete (window as any).NexoraxVoiceRouter;
      clearInterval(brandingCheckInterval);
    };
  }, []);

  // Handlers for NexoraX Agent Task Deployment Panel
  const handleExecuteAgentTask = (taskIndex: number) => {
    if (activeAgentTask !== null) return;
    
    setActiveAgentTask(taskIndex);
    setAgentTaskProgress(0);
    
    const taskNames = [
      "Autonomous Universal Lead Discovery & Scraping Pipeline",
      "Cross-Platform Deep Outreach & Closing Optimization",
      "Secure Payment Provider & GoCardless Automated Financial Binding",
      "Automated Multi-Tier Resource Provisioning"
    ];

    const taskLogs = [
      // Task 1
      [
        "[INIT] Bootstrapping lead hunter drones across 18 distributed digital sectors...",
        "[SCANNING] Sweeping regional SME platforms & high-density hospitality zones...",
        "[EXTRACTION] Parsing public records, phone indexes, and emails for key stakeholders...",
        "[COMPILING] Formatting raw discovery data. Filtering out secure high-authority nodes...",
        "[SUCCESS] 42 high-value targets found! Ledger entries registered to Google Drive NexoraX Resource Library via CloudSync-02."
      ],
      // Task 2
      [
        "[TEMPLATING] Generating absolute personalized high-conversion Strike Pitches...",
        "[INJECTING] Attaching authorized secure Stripe Payment URL: https://buy.stripe.com/8x2bIU2t59oYgTtfxQaMU00",
        "[ROUTING] Preparing multi-channel queue (WhatsApp / Telegram / support@nexoraxdigital.co.uk)...",
        "[ENGAGING] Dispatching automated outbound strike pitches with 0.8s delivery reaction standard...",
        "[COMPLETED] Closing sequence active. Real-time followups registered under Director's Vanguard-01 mandate."
      ],
      // Task 3
      [
        "[LEDGER_CHECK] Accessing Secure Payment Provider ledger configurations (Sort: Verified Payment Configuration, Account: Secure Billing Provider)...",
        "[LEVY] Splitting 1% Perpetual 3% Community Allocation among Charity 1, Charity 2, & Charity 3...",
        "[TAX_RESERVE] Locking 10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee) inside automated escrow vault...",
        "[GOCARDLESS] Binding recurring direct debit webhooks under secure UK / global regulations...",
        "[VERIFICATION] Handshake established. Automated financial flow linked with live ledger summaries."
      ],
      // Task 4
      [
        "[SANDBOX] Spinning up isolated enterprise-grade Linux container sandbox environments...",
        "[WORKSPACE] Auto-provisioning business emails (@nexorax.co.uk) with eIDAS compliance...",
        "[MIGRATION] Linking remote node state files for sponsorship tracking protocols...",
        "[DEPLOYMENT] Replicating original source directories to gdrive:Nexorax_Ecosystem_Backups/Source_Core_V2_LivingBrain...",
        "[INTEGRITY] Build successfully verified. Branding lock engine active on NEXORAX_AGENT_IDENTITY."
      ]
    ];

    const currentLogs = taskLogs[taskIndex];
    setAgentLogs([`[MANDATE] Init Core Command: "${taskNames[taskIndex]}"`]);

    let step = 0;
    const interval = setInterval(() => {
      step += 1;
      const progress = step * 20; // 5 steps (20, 40, 60, 80, 100)
      setAgentTaskProgress(progress);
      
      const logLine = currentLogs[step - 1];
      if (logLine) {
        setAgentLogs(prev => [...prev, logLine]);
      }

      if (step >= 5) {
        clearInterval(interval);
        setActiveAgentTask(null);
        setCompletedAgentTasks(prev => {
          const next = [...prev];
          next[taskIndex] = true;
          return next;
        });
        setSuccessToast(`NEXORAX AGENT: "${taskNames[taskIndex]}" EXECUTED SUCCESSFULLY`);
        setTimeout(() => setSuccessToast(null), 3000);
      }
    }, 1000);
  };

  // Recalculate billing values
  useEffect(() => {
    const amt = parseFloat(grossHarvestInput) || 0;
    // 1% 3% Community Allocation
    const levy = amt * 0.01;
    // 33.33% split of the 1% Levy for Charity 1 (Dunfermline), Charity 2 (Global), Charity 3 (UK)
    const c1 = levy * 0.33333;
    const c2 = levy * 0.33333;
    const c3 = levy * 0.33333;
    // Remaining balance
    const remainingAfterLevy = amt - levy;
    // 10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee)
    const tax = remainingAfterLevy * 0.20;
    // Remaining Net
    const net = remainingAfterLevy - tax;

    setLevyOutput(levy);
    setCharitySplit({ c1, c2, c3 });
    setTaxOutput(tax);
    setNetSettlementOutput(net);
  }, [grossHarvestInput]);

  // Handle Voice Generation from Node server using gemini-3.1-flash-tts-preview
  const speakText = (text: string, voiceName: string): Promise<void> => {
    return new Promise<void>(async (resolve) => {
      if (!voiceEnabled) {
        resolve();
        return;
      }
      try {
        setIsPlayingAudio(true);
        setLastSpeechText(text);
        if ("speechSynthesis" in window) {
          window.speechSynthesis.cancel();
        }
        
        const res = await fetch("/api/generate-voice", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ 
            text: text, 
            voiceName: voiceName || "Zephyr" 
          })
        });
        const data = await res.json();
        if (data.status === "success" && data.audio) {
          if (audioRef.current) {
            audioRef.current.pause();
          }
          const wavUrl = pcmBase64ToWavDataUrl(data.audio, 24000);
          const audio = new Audio(wavUrl);
          audioRef.current = audio;
          audio.onended = () => {
            setIsPlayingAudio(false);
            resolve();
          };
          audio.onerror = () => {
            setIsPlayingAudio(false);
            resolve();
          };
          audio.play().catch(e => {
            console.warn("Audio play blocked:", e);
            setIsPlayingAudio(false);
            resolve();
          });
        } else {
          throw new Error("Voice API returned empty stream");
        }
      } catch (err) {
        console.warn("Real TTS synthesis failed. Falling back to clean browser SpeechSynthesis.", err);
        if (typeof window !== "undefined" && "speechSynthesis" in window) {
          window.speechSynthesis.cancel();
          const utterance = new SpeechSynthesisUtterance(text);
          
          const isMaleVoice = voiceName && (
            voiceName.toLowerCase().includes("charon") || 
            voiceName.toLowerCase().includes("fenrir") || 
            voiceName.toLowerCase().includes("puck") || 
            voiceName.toLowerCase().includes("NexoraX") ||
            voiceName.toLowerCase().includes("director")
          );
          
          utterance.rate = isMaleVoice ? 0.82 : 1.0;
          utterance.pitch = isMaleVoice ? 0.65 : 1.0;
          
          const voices = window.speechSynthesis.getVoices();
          let targetVoice = null;
          
          if (isMaleVoice) {
            // Find English male voices first
            targetVoice = voices.find(v => {
              const n = v.name.toLowerCase();
              return (
                n.includes("male") ||
                n.includes("david") ||
                n.includes("george") ||
                n.includes("mark") ||
                n.includes("daniel") ||
                n.includes("richard") ||
                n.includes("james") ||
                n.includes("peter")
              ) && v.lang.startsWith("en-");
            });
            if (!targetVoice) {
              targetVoice = voices.find(v => v.lang.startsWith("en-"));
            }
          } else {
            // Find matched female English voice as standard default fallback
            targetVoice = voices.find(v => {
              const n = v.name.toLowerCase();
              return (n.includes("female") || n.includes("zira") || n.includes("samantha") || n.includes("karen") || n.includes("hazel")) && v.lang.startsWith("en-");
            });
          }
          
          if (!targetVoice) {
            targetVoice = voices.find(v => v.lang.startsWith("en-GB") || v.lang.startsWith("en-US") || v.name.toLowerCase().includes("female")) || voices[0];
          }
          
          if (targetVoice) {
            utterance.voice = targetVoice;
          }
          
          utterance.onend = () => {
            setIsPlayingAudio(false);
            resolve();
          };
          utterance.onerror = () => {
            setIsPlayingAudio(false);
            resolve();
          };
          window.speechSynthesis.speak(utterance);
        } else {
          // Fallback: wait 3 seconds to emulate speech length
          setTimeout(() => {
            setIsPlayingAudio(false);
            resolve();
          }, 3000);
        }
      }
    });
  };

  // Run the concurrent taskforce simulation on the selected squad
  const runSquadSimulation = async (squadId: string) => {
    const squad = squads.find(s => s.id === squadId);
    if (!squad) return;

    setIsSimulating(true);
    setSimulationLogs([]);
    
    const logs = [
      { id: "1", sender: "SQUAD MONITOR", text: `NODE SIGNAL: CONCURRENT SPEECH INTERLOCK LAUNCHED`, type: "info" as const, timestamp: new Date().toLocaleTimeString() },
      { id: "2", sender: "TACTICAL DIRECTIVE", text: `CO-OPERATIVE OBJECTIVE: ${squad.tacticalObjective.toUpperCase()}`, type: "info" as const, timestamp: new Date().toLocaleTimeString() },
      { id: "3", sender: "SECURE TUNNELS MAPPED", text: "CloudSync-02 ENFORCING OFF-SITE METADATA BACKUPS TO MOUNTED G-DRIVE", type: "info" as const, timestamp: new Date().toLocaleTimeString() }
    ];
    setSimulationLogs(logs);

    // Formulate modular conversational dialogue script based on squad objective
    let script: { sender: string; voice: string; text: string; delay: number }[] = [];

    if (squad.id === "squad-recruit-strike") {
      script = [
        {
          sender: "Lead Creator Assistant Pro",
          voice: "Zephyr",
          text: "Verification core is online. I have compiled candidates Eleanor, Helen, and Claire. Preparing direct pipeline parameters for UK NexoraX billing records eligibility.",
          delay: 3500
        },
        {
          sender: "Lead Qualification Specialist",
          voice: "Charon",
          text: "National insurance NI Secure Credential Stored Server-Side is compliant. The 15-2-8 minute God-Mode Funnel is engaged. Bypassing administrative systems. Candidate details vetted.",
          delay: 4000
        },
        {
          sender: "Appointment Scheduler Specialist",
          voice: "Kore",
          text: "Calendar slots matching. Vetting successful. Securely booked 15-minute technical interview with Director Lead Director on line Private Data Hidden. Confirmed.",
          delay: 4000
        },
        {
          sender: "Lead Creator Assistant Pro",
          voice: "Zephyr",
          text: "NexoraX deployment finalized. All telemetry data mirrored to Google Drive NexoraX Resource Library. Core operation secured.",
          delay: 3000
        }
      ];
    } else if (squad.id === "squad-customer-qc") {
      script = [
        {
          sender: "Lead Qualification Specialist",
          voice: "Charon",
          text: "Indexing client website metadata. All assets found in server folders. Readying catalog mapping filters for subnodes.",
          delay: 3500
        },
        {
          sender: "Customer Support Specialist",
          voice: "Kore",
          text: "Active web dialog initialized. Running real-time catalog lookups to support general business inquiries. Verifying SSL compliance status.",
          delay: 3800
        },
        {
          sender: "Lead Qualification Specialist",
          voice: "Charon",
          text: "Indexing completed successfully. SLA performance verified under 0.8s. Standard operating system criteria satisfied.",
          delay: 3000
        }
      ];
    } else if (squad.id === "squad-bank-extraction") {
      script = [
        {
          sender: "Inbound & Outbound Specialist",
          voice: "Fenrir",
          text: "Broadcasting on 08000 622827 absolute communication channel. Accessing invoice registries. Telemetry logs connected to SecurePayment Node.",
          delay: 3500
        },
        {
          sender: "Customer Support Specialist",
          voice: "Kore",
          text: "Ledger status active. Deducting the 1% 3% Community Allocation for global charities, and extracting the 10% Maintenance & Hosting Fee 10% Maintenance & Hosting Fee.",
          delay: 4000
        },
        {
          sender: "Customer Support Specialist",
          voice: "Kore",
          text: "Revenue extracted. Net settlement routed successfully to Secure Payment Provider (Sort Code: Verified Payment Configuration | Account Number: Secure Billing Provider). Logged.",
          delay: 3500
        }
      ];
    } else if (squad.id === "squad-government-compliance") {
      script = [
        {
          sender: "Lead Creator Assistant Pro",
          voice: "Zephyr",
          text: "Initializing UK government compliance audit check. Verifying Amazonia Grid system infrastructure and NexoraX billing records files.",
          delay: 3500
        },
        {
          sender: "Knowledge Base Specialist",
          voice: "Zephyr",
          text: "All files compiled. UK regulatory constraints matched perfectly. Mirroring living dossiers to Google Drive backup archives. Done.",
          delay: 3800
        }
      ];
    } else {
      // Dynamic custom squad dialogue
      script = [
        {
          sender: "Lead Creator Assistant Pro",
          voice: "Zephyr",
          text: `Initializing custom tactical taskforce: [${squad.name}]. Mapping special parameters and preparing nodes.`,
          delay: 3500
        },
        {
          sender: "Lead Qualification Specialist",
          voice: "Charon",
          text: `Executing custom objective: ${squad.tacticalObjective}. Verifying structural dependencies under 0.8s latency constraints.`,
          delay: 4000
        },
        {
          sender: "Lead Creator Assistant Pro",
          voice: "Zephyr",
          text: "Workflow simulation resolved. Mirroring session logs directly to Google Drive via CloudSync-02.",
          delay: 3000
        }
      ];
    }

    // Sequentially play dialogues and speak
    for (let i = 0; i < script.length; i++) {
      const step = script[i];
      setActiveSpeakingNode(step.sender);
      
      const newLog = {
        id: `dialog-step-${i}`,
        sender: step.sender.toUpperCase(),
        text: step.text,
        type: "message" as const,
        timestamp: new Date().toLocaleTimeString(),
        voice: step.voice
      };
      
      setSimulationLogs(prev => [...prev, newLog]);

      // Trigger actual speech synth via back-end endpoint
      if (voiceEnabled) {
        await speakText(step.text, step.voice);
      } else {
        // Wait script delay
        await new Promise(resolve => setTimeout(resolve, step.delay));
      }
    }

    // Success close log
    setActiveSpeakingNode(null);
    setSimulationLogs(prev => [
      ...prev,
      {
        id: "final-success-log",
        sender: "SYSTEM STATUS",
        text: "TASKFORCE OBJECTIVE COMPLETED SUCCESSFULLY. LEDGER REPORT MERGED TO IMPERIAL WAULT.",
        type: "success" as const,
        timestamp: new Date().toLocaleTimeString()
      }
    ]);
    setIsSimulating(false);

    // Mark squad as completed
    setSquads(prev => prev.map(s => s.id === squadId ? { ...s, status: "SUCCESS" } : s));
  };

  // Deploy a new custom squad registry
  const handleDeployCustomSquad = () => {
    if (!customSquadName || !customSquadObjective || customSquadMembers.length < 2) {
      alert("Validation failed. Please define a title, cooperative objective, and interlock at least 2 specialists.");
      return;
    }

    const newSquad: VoiceSquad = {
      id: `custom-squad-${Date.now()}`,
      name: customSquadName,
      description: customSquadDesc || "Custom structured cooperative taskforce designed via the public console.",
      specialistIds: customSquadMembers,
      tacticalObjective: customSquadObjective,
      status: "STANDBY",
      isCustom: true
    };

    setSquads(prev => [...prev, newSquad]);
    setSelectedSquadId(newSquad.id);
    
    // Clear out
    setCustomSquadName("");
    setCustomSquadDesc("");
    setCustomSquadObjective("");
    setCustomSquadMembers([]);
    setShowBuilder(false);
  };

  // Delete a custom squad
  const handleDeleteSquad = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setSquads(prev => prev.filter(s => s.id !== id));
    if (selectedSquadId === id) {
      setSelectedSquadId("squad-recruit-strike");
    }
  };

  // Run a live voice line scanner sweep
  const runVoiceFrequencySweep = () => {
    setIsSweeping(true);
    setSweepProgress(0);
    setSweepResult(["[STANDBY] Requesting Planet Telecom voice interlocks..."]);

    const logs = [
      "[0.1s] Sweeping 08000 622827 Planet Telecom voice pipeline...",
      "[0.3s] Signal captured at high amplitude on 180Hz channel.",
      "[0.5s] Dial-in frequency clean. Zero signal interference detected.",
      "[0.6s] Securing data routing line blocks through SIP virtual gateways.",
      "[0.7s] 0.8s Latency speed targets validated. Performance meets corporate standard.",
      "[0.8s] Handshake completed with Secure Payment Provider transaction validators.",
      "[0.8s] Mirror path verified: /var/www/nexorax_source <-> Google Drive NexoraX Resource Library."
    ];

    let current = 0;
    const interval = setInterval(() => {
      current += 15;
      if (current >= 100) {
        current = 100;
        clearInterval(interval);
        setIsSweeping(false);
        setSweepResult(prev => [...prev, ...logs, "[SUCCESS] ABSOLUTE DIRECT ROUTE GUARANTEED. STANDBY TO RECEIVE MANDATES."]);
      }
      setSweepProgress(current);
    }, 250);
  };

  // Onboard Custom Node Submission
  const handleOnboardSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadName || !leadEmail) return;

    setSuccessToast(`ONBOARD REQUEST RECIEVED. SECURE PENDING PROTOCOL BOOTSTAPPED. DIRECT CELL: Private Data Hidden PINNED.`);
    setTimeout(() => {
      setSuccessToast(null);
    }, 5000);

    setLeadName("");
    setLeadEmail("");
    setLeadPhone("");
  };

  return (
    <div id="public-voice-matrix-portal" className="w-full max-w-7xl mx-auto space-y-12 text-slate-300 font-sans relative pb-20">
      
      {/* Absolute Preamble Header */}
      <div className="text-center font-mono text-[10px] md:text-xs text-cyan-400 font-black tracking-[0.25em] uppercase select-none opacity-80 max-w-4xl mx-auto border-b border-cyan-500/10 pb-4">
        {preamble}
      </div>

      {/* Main Beyond the Moon Title Banner */}
      <div className="text-center relative py-8 overflow-hidden rounded-3xl bg-black/40 border border-white/5 shadow-2xl">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,195,255,0.15),transparent_80%)] pointer-events-none" />
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl pointer-events-none animate-pulse" style={{ animationDuration: '8s' }} />
        
        <div className="relative z-10 space-y-4 px-6">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-cyan-500/10 border border-cyan-400/30 rounded-full text-cyan-400 text-[10px] font-mono font-bold tracking-widest uppercase">
            <Volume2 className="w-3.5 h-3.5 animate-pulse" />
            <span>NEXORAX NEXORAX VOICE PLATFORM</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-black text-white tracking-tighter uppercase drop-shadow-[0_0_20px_rgba(255,255,255,0.1)]">
            AI VOICE SQUAD MATRIX
          </h2>
          <p className="font-mono text-[11px] md:text-xs text-slate-400 uppercase tracking-wider max-w-3xl mx-auto leading-relaxed">
            Provision, combine, and execute elite virtual voice taskforces that conduct live client negotiations, catalog websites, audit transaction structures, and settle bank ledgers. Take your communications beyond the moon.
          </p>
        </div>
      </div>

      {/* NexoraX Live Voice Interface - Neural Waveform Node */}
      <NexoraXVoiceAssistant />

      {/* Voice Command Manager Control Node */}
      <VoiceCommandManager />

      {/* Interactive Target Segment Tabs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-black/40 border border-slate-900 p-2 rounded-2xl">
        {[
          { key: 'individual', title: 'INDIVIDUALS', desc: 'Sponsorships & Secure Invoice Register Vetting', icon: <Users className="w-4 h-4" /> },
          { key: 'business', title: 'BUSINESSES', desc: 'Web Cataloging & Interactive Q&A', icon: <Zap className="w-4 h-4" /> },
          { key: 'corporate', title: 'CORPORATES', desc: 'Secure Ledger & Income Extraction', icon: <Building className="w-4 h-4 text-emerald-400" /> },
          { key: 'government', title: 'GOVERNMENTS', desc: 'NexoraX billing records Audit Files', icon: <Award className="w-4 h-4 text-amber-400" /> }
        ].map((tab) => {
          const isActive = activeSegment === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => {
                if (!isSimulating) {
                  setActiveSegment(tab.key as any);
                }
              }}
              disabled={isSimulating}
              className={`p-4 rounded-xl text-left border transition-all relative flex flex-col justify-between ${
                isSimulating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
              } ${
                isActive 
                  ? 'bg-gradient-to-br from-cyan-950/20 to-indigo-950/20 border-cyan-500/50 shadow-[0_0_20px_rgba(0,195,255,0.15)] text-white' 
                  : 'bg-black/25 border-transparent text-slate-400 hover:border-slate-800'
              }`}
            >
              <div className="flex items-center justify-between w-full">
                <span className={`p-2 rounded-lg ${isActive ? 'bg-cyan-500/20 text-cyan-400' : 'bg-slate-950 text-slate-500'}`}>
                  {tab.icon}
                </span>
                {isActive && <div className="w-2 h-2 rounded-full bg-cyan-400 animate-ping" />}
              </div>
              <div className="mt-4">
                <span className="block text-xs font-mono font-black tracking-widest uppercase">{tab.title}</span>
                <span className="block text-[9px] text-slate-500 font-mono mt-1 uppercase">{tab.desc}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Main Interactive Matrix Dashboard */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
        
        {/* LEFT PANEL: ROSTER AND DIRECTIVES (Col-span 5) */}
        <div className="lg:col-span-5 flex flex-col space-y-6">
          <div className="bg-[#02050f] border border-slate-900 rounded-3xl p-6 flex flex-col space-y-4">
            <div className="flex items-center justify-between border-b border-slate-900 pb-3">
              <h3 className="text-white text-sm font-mono font-black uppercase tracking-wider flex items-center gap-2">
                <Server className="w-4 h-4 text-cyan-500" />
                <span>TASKFORCE REGISTRY</span>
              </h3>
              <button
                onClick={() => setShowBuilder(!showBuilder)}
                className="px-2.5 py-1.5 bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 rounded text-[9px] font-mono tracking-widest font-black uppercase hover:bg-cyan-500 hover:text-black transition cursor-pointer"
              >
                {showBuilder ? "[ CLOSE BUILDER ]" : "[ PROVISION CUSTOM... ]"}
              </button>
            </div>

            {/* Custom squad builder dropdown integration */}
            <AnimatePresence>
              {showBuilder && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="bg-black/45 border border-cyan-500/15 p-4 rounded-xl space-y-3 overflow-hidden"
                >
                  <span className="text-[9px] text-cyan-400 font-mono font-black tracking-widest block uppercase">NODE DISPATCH MATRIX BUILDER</span>
                  <div className="space-y-3 font-mono text-[10px]">
                    <input
                      type="text"
                      placeholder="CUSTOM SQUAD TITLE (E.G. OMEGA LOGISTIC CLOSERS)"
                      value={customSquadName}
                      onChange={(e) => setCustomSquadName(e.target.value)}
                      className="w-full bg-slate-950 border border-slate-900 rounded p-2 text-white placeholder-slate-600 uppercase"
                    />
                    <input
                      type="text"
                      placeholder="CAMPAIGN MISSION DETAILS"
                      value={customSquadDesc}
                      onChange={(e) => setCustomSquadDesc(e.target.value)}
                      className="w-full bg-slate-950 border border-slate-900 rounded p-2 text-white placeholder-slate-600"
                    />
                    <textarea
                      placeholder="CONCURRENT MANDATE OBJECTIVE..."
                      value={customSquadObjective}
                      onChange={(e) => setCustomSquadObjective(e.target.value)}
                      rows={2}
                      className="w-full bg-slate-950 border border-slate-900 rounded p-2 text-white placeholder-slate-600 uppercase"
                    />
                    <div className="space-y-1">
                      <span className="text-slate-500 text-[8px] font-bold block uppercase mb-1">INTERLOCK COMPONENT SPECIALISTS:</span>
                      <div className="grid grid-cols-2 gap-1.5 max-h-24 overflow-y-auto custom-scrollbar bg-slate-950 p-2 rounded border border-slate-900/60">
                        {PUBLIC_SPECIALISTS.map(spec => {
                          const isChecked = customSquadMembers.includes(spec.id);
                          return (
                            <label key={spec.id} className="flex items-center gap-1.5 p-1 hover:bg-slate-900 rounded text-[8px] font-bold uppercase cursor-pointer select-none text-slate-300">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                className="accent-cyan-500 rounded text-cyan-500 cursor-pointer"
                                onChange={() => {
                                  if (isChecked) {
                                    setCustomSquadMembers(prev => prev.filter(id => id !== spec.id));
                                  } else {
                                    setCustomSquadMembers(prev => [...prev, spec.id]);
                                  }
                                }}
                              />
                              <span>{spec.name.split(' ')[0]} Node</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                    <button
                      onClick={handleDeployCustomSquad}
                      className="w-full py-2 bg-gradient-to-r from-cyan-500 to-indigo-500 text-black text-xs font-black uppercase rounded shadow-lg tracking-widest cursor-pointer"
                    >
                      COMPILE NEXORAX SQUAD
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Squads Scroll Roster */}
            <div className="space-y-3">
              {squads.map((sq) => {
                const isSelected = sq.id === selectedSquadId;
                const isSegmentRec = segmentSquadMap[activeSegment] === sq.id;
                
                return (
                  <div
                    key={sq.id}
                    onClick={() => {
                      if (!isSimulating) {
                        setSelectedSquadId(sq.id);
                      }
                    }}
                    className={`p-4 rounded-xl border transition-all duration-300 relative ${
                      isSimulating ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                    } ${
                      isSelected 
                        ? 'bg-cyan-950/10 border-cyan-500/40 shadow-[0_0_15px_rgba(0,195,255,0.1)] text-white' 
                        : 'bg-black/35 border-slate-900 text-slate-400 hover:border-slate-800'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="bg-slate-900 border border-slate-850 px-1.5 py-0.5 text-cyan-400 rounded text-[7px] font-mono tracking-widest uppercase font-extrabold flex items-center gap-1">
                            <Workflow className="w-3.5 h-3.5" />
                            {sq.isCustom ? "CUSTOM_TASKFORCE" : "PREBUILT"}
                          </span>
                          {isSegmentRec && (
                            <span className="bg-purple-900/30 border border-purple-500/20 text-purple-400 text-[7px] font-mono font-bold px-1 rounded uppercase">
                              RECOMMENDED FOR GIVEN TIERS
                            </span>
                          )}
                          {sq.status === 'SUCCESS' && (
                            <span className="text-emerald-400 text-[7px] font-mono font-bold flex items-center gap-0.5 bg-emerald-500/10 border border-emerald-500/20 px-1 rounded">
                              <CheckCircle2 className="w-2.5 h-2.5" /> SECURED
                            </span>
                          )}
                        </div>
                        <h4 className={`text-xs font-sans font-extrabold uppercase mt-2 tracking-tight ${isSelected ? 'text-white' : 'text-slate-200'}`}>
                          {sq.name}
                        </h4>
                      </div>

                      {sq.isCustom && !isSimulating && (
                        <button
                          onClick={(e) => handleDeleteSquad(e, sq.id)}
                          className="p-1 hover:bg-rose-500/20 hover:text-rose-400 text-slate-500 rounded transition-colors cursor-pointer"
                          title="Decommit Force Squad"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>

                    <p className="text-[10px] text-slate-400 mt-2 line-clamp-2 leading-relaxed uppercase">
                      {sq.description}
                    </p>

                    {/* Interactive Sandbox Wiretap Status */}
                    <div className="mt-2.5 flex items-center justify-between text-[8px] font-mono bg-black/40 border border-slate-900/60 px-2.5 py-1.5 rounded-lg">
                      <span className="text-slate-500 font-bold uppercase">SANDBOX STREAM:</span>
                      <span 
                        id={`sandbox-status-${sq.name.replace(/\s+/g, '-')}`} 
                        className="text-amber-500 font-black animate-pulse uppercase tracking-wider pl-1"
                      >
                        ● COGNITIVE STANDBY
                      </span>
                    </div>

                    {/* Member indicators */}
                    <div className="mt-3.5 pt-2.5 border-t border-slate-900/60 flex items-center justify-between text-[8px] font-mono">
                      <div className="flex items-center gap-1.5">
                        <span className="text-slate-500 font-bold uppercase">NODES:</span>
                        <div className="flex gap-1 flex-wrap">
                          {sq.specialistIds.map((id) => {
                            const specObj = PUBLIC_SPECIALISTS.find(s => s.id === id);
                            return (
                              <span 
                                key={id}
                                className="px-1.5 py-0.5 bg-slate-900 border border-slate-850 rounded text-[7px] text-slate-300 font-semibold"
                                title={specObj?.name || ""}
                              >
                                {specObj ? specObj.name.split(' ')[0] : 'Node'}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* RIGHT PANEL: CONCURRENT AUDIO CONSOLE TERMINAL (Col-span 7) */}
        <div className="lg:col-span-7 flex flex-col justify-between space-y-6">
          {(() => {
            const selectedSquad = squads.find(s => s.id === selectedSquadId);
            if (!selectedSquad) return (
              <div className="bg-[#01040a] border border-slate-900 rounded-3xl p-6 h-full flex flex-col items-center justify-center text-center font-mono">
                <Radio className="w-8 h-8 text-slate-800 animate-pulse mb-3" />
                <span className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">SELECT ACTIVE MATRIX TO ACTIVATE VOICE MODULATOR</span>
              </div>
            );

            return (
              <div id="voice-simulation-panel" className="bg-[#02050f] border border-slate-900 rounded-3xl p-6 flex flex-col justify-between h-full space-y-6 relative min-h-[480px]">
                {/* Embedded live scanning indicator */}
                <div className="absolute top-3 right-4 font-mono text-[8px] text-cyan-400/60 uppercase tracking-widest flex items-center gap-1.5 select-none">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse inline-block" />
                  <span>VOICE CHANNELS: 18-SECTOR READY</span>
                </div>

                <div className="space-y-4">
                  <div>
                    <span className="text-slate-500 font-mono text-[80%] uppercase tracking-wider font-extrabold pb-1">SELECTED CO-OPERATIVE DIRECTIVES</span>
                    <h3 className="text-white text-lg font-sans font-black uppercase mt-1 tracking-tight">
                      {selectedSquad.name}
                    </h3>
                    <div className="bg-[#00050e] border border-cyan-500/10 p-3.5 rounded-xl mt-2 text-[10px] text-slate-400 font-mono leading-relaxed uppercase">
                      <div className="font-bold text-cyan-400 text-[8px] tracking-widest uppercase mb-1">TACTICAL MANDATE OBJECTIVE:</div>
                      {selectedSquad.tacticalObjective}
                    </div>

                    {/* Sandbox Integration Control */}
                    <div className="mt-4 bg-black/45 border border-slate-900 rounded-xl p-3 flex flex-col sm:flex-row items-center justify-between gap-3">
                      <div className="flex flex-col text-left font-mono">
                        <span className="text-[8px] text-slate-500 font-bold uppercase">SANDBOX SIMULATOR CHANNEL:</span>
                        <span className="text-[9px] text-[#e840f3] font-bold uppercase tracking-wider mt-0.5">
                          SECURE RECONNAISSANCE TUNNEL
                        </span>
                      </div>
                      
                      <button
                        onClick={() => {
                          if ((window as any).NexoraxOS && (window as any).NexoraxOS.toggleVoiceSandbox) {
                            (window as any).NexoraxOS.toggleVoiceSandbox(selectedSquad.name);
                            setSuccessToast(`SANDBOX VERIFIED: ESTABLISHED VOICE INTERLOCK MATRIX WITH ${selectedSquad.name.toUpperCase()}`);
                            setTimeout(() => setSuccessToast(null), 3500);
                          }
                        }}
                        className="px-3.5 py-1.5 bg-amber-500/10 border border-amber-500/30 hover:bg-amber-500 hover:text-black rounded text-[9px] font-mono text-amber-500 hover:shadow-[0_0_15px_rgba(245,158,11,0.2)] font-black uppercase tracking-wider transition-all duration-300 cursor-pointer flex items-center gap-1.5"
                      >
                        <Radio className="w-3.5 h-3.5 animate-pulse" />
                        <span>ESTABLISH SANDBOX AUDIO CHANNEL</span>
                      </button>
                    </div>
                  </div>

                  {/* ACTIVE VOICE DIALOG PANEL */}
                  <div className="flex flex-col space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[8px] text-slate-500 font-mono font-black uppercase tracking-widest">LIVE CONVERSATIONAL STREAM DIALOGUES</span>
                      <label className="flex items-center gap-1.5 font-mono text-[8px] text-cyan-400 uppercase cursor-pointer">
                        <input
                          type="checkbox"
                          checked={voiceEnabled}
                          onChange={(e) => setVoiceEnabled(e.target.checked)}
                          className="accent-cyan-500"
                        />
                        <span>ENABLE AI TTS MODULATION</span>
                      </label>
                    </div>

                    <div className="bg-black/90 border border-slate-950 rounded-2xl p-4 h-64 overflow-y-auto custom-scrollbar space-y-3 font-mono text-[10px]">
                      {simulationLogs.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-center text-slate-600 uppercase space-y-2 select-none">
                          <Cpu className="w-8 h-8 text-slate-800 animate-spin" style={{ animationDuration: '5s' }} />
                          <div className="text-[9px] text-slate-500 font-black">SQUAD STATUS: DECOMMIT STANDBY</div>
                          <p className="text-[8px] text-slate-600 max-w-sm">
                            Click simulation below to watch taskforce nodes initiate live virtual Q&A or contract negotiations. Enable AI speech to play realistic neural voice waves in real-time.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {simulationLogs.map((log) => {
                            const isInfo = log.type === 'info';
                            const isSuccess = log.type === 'success';
                            const isSpeaking = activeSpeakingNode === log.sender;
                            
                            return (
                              <div
                                key={log.id} 
                                className={`p-3 rounded-xl border leading-relaxed transition-all duration-300 ${
                                  isSpeaking 
                                    ? 'bg-cyan-950/20 border-cyan-400 text-white shadow-[0_0_15px_rgba(0,240,255,0.15)] scale-[1.01]' 
                                    : isInfo
                                      ? 'bg-slate-900/40 border-slate-850 text-slate-500 text-[9px]'
                                      : isSuccess
                                        ? 'bg-emerald-950/15 border-emerald-500/30 text-emerald-300 font-bold'
                                        : 'bg-black/60 border-slate-900 text-slate-300'
                                }`}
                              >
                                <div className="flex items-center justify-between border-b border-white/5 pb-1.5 mb-1.5 text-[7px] font-extrabold uppercase tracking-widest">
                                  <span className={isSpeaking ? "text-cyan-400 font-black animate-pulse" : isInfo ? "text-cyan-600" : "text-slate-400"}>
                                    {log.sender}
                                  </span>
                                  <span>{log.timestamp}</span>
                                </div>
                                <div className="uppercase tracking-wide">{log.text}</div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Simulated Audio Waves Indicator */}
                <AnimatePresence>
                  {isPlayingAudio && (
                    <motion.div 
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 5 }}
                      className="bg-cyan-950/10 border border-cyan-400/30 rounded-xl p-3 flex items-center justify-between text-cyan-400 text-[10px] font-mono uppercase"
                    >
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-cyan-400 animate-ping" />
                        <span>PLAYING SYNTHETIC VOICE MODULATION...</span>
                      </div>
                      <div className="flex gap-1 h-3 items-end">
                        <span className="w-1 bg-cyan-400 animate-pulse h-full" style={{ animationDelay: '0.1s' }} />
                        <span className="w-1 bg-cyan-400 animate-pulse h-1/2" style={{ animationDelay: '0.3s' }} />
                        <span className="w-1 bg-cyan-400 animate-pulse h-3/4" style={{ animationDelay: '0.2s' }} />
                        <span className="w-1 bg-cyan-400 animate-pulse h-2/3" style={{ animationDelay: '0.4s' }} />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Simulation control Button */}
                <div className="pt-2">
                  <button
                    disabled={isSimulating}
                    onClick={() => {
                      if (typeof window !== "undefined" && "speechSynthesis" in window) {
                        try {
                          window.speechSynthesis.cancel();
                          const silentObj = new SpeechSynthesisUtterance(" ");
                          silentObj.volume = 0;
                          window.speechSynthesis.speak(silentObj);
                        } catch (e) {
                          console.warn("SpeechSynthesis silent prime bypassed", e);
                        }
                      }
                      runSquadSimulation(selectedSquad.id);
                    }}
                    className={`w-full py-3.5 bg-gradient-to-r from-cyan-500 internally to-indigo-500 text-black font-extrabold uppercase tracking-widest text-xs rounded-xl shadow-[0_0_15px_rgba(0,240,255,0.25)] transition-all duration-300 flex items-center justify-center gap-1.5 cursor-pointer hover:shadow-[0_0_25px_rgba(0,195,255,0.4)] ${
                      isSimulating ? 'opacity-50 cursor-not-allowed filter grayscale' : 'hover:scale-[1.005]'
                    }`}
                  >
                    {isSimulating ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>SQUAD INTER-LOCKING CONCURRENT DIRECTIVES...</span>
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 fill-black text-black ml-0.5" />
                        <span>INITIALIZE TASKFORCE AUDITION SIMULATOR</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })()}
        </div>

      </div>

      {/* DUAL LEDGER AND TAX CALCULATOR */}
      <div id="financial-ledger-matrix" className="bg-[#02050f] border border-slate-900 rounded-3xl p-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-80 bg-emerald-500/5 rounded-full blur-3xl pointer-events-none" />
        
        <div className="border-b border-slate-900 pb-4 mb-6">
          <div className="text-emerald-400 font-bold text-[9px] uppercase tracking-[0.3em] flex items-center gap-1.5 font-mono">
            <Database className="w-4 h-4" />
            <span>NEXORAX NEXORAX PROTOCOL: EXTRACTION ENGINE</span>
          </div>
          <h3 className="text-xl md:text-2xl font-black text-white tracking-tight uppercase mt-1">
            FINANCIAL PROTOCOLS & LEDGER EXTRACTION
          </h3>
          <p className="text-slate-400 font-mono text-[10px] leading-relaxed uppercase mt-1">
            Simulate our trademark revenue division: extract the 1% 3% Community Allocation (instantly splitting equally across regional/global charities) and the 20% Lifetime 10% Maintenance & Hosting Fee. Net Settlements are transferred directly to Secure Payment Provider Sort Code Verified Payment Configuration, Account Number Secure Billing Provider.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
          
          {/* Input values */}
          <div className="space-y-4 font-mono text-xs">
            <div className="flex flex-col space-y-1.5">
              <label className="text-slate-400 uppercase tracking-widest font-black">Enter Raw Daily Gross Harvest (£)</label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 font-bold text-sm">£</span>
                <input
                  type="number"
                  value={grossHarvestInput}
                  onChange={(e) => setGrossHarvestInput(e.target.value)}
                  placeholder="E.G. 350000"
                  className="w-full bg-black border border-slate-800 rounded-xl pl-9 pr-4 py-3 text-white text-sm focus:outline-none focus:border-emerald-500 transition-all font-bold"
                />
              </div>
            </div>

            <div className="bg-black/40 border border-slate-900 p-4 rounded-xl space-y-3">
              <span className="text-[10px] text-slate-500 font-bold block uppercase border-b border-slate-900 pb-2">Secure Payment Provider TARGET DEPOSIT FILE</span>
              <div className="grid grid-cols-2 gap-4 text-[9px] text-slate-400 uppercase">
                <div>
                  <span className="block text-slate-500">SORT CODE</span>
                  <span className="font-bold text-white tracking-widest text-xs">Verified Payment Configuration</span>
                </div>
                <div>
                  <span className="block text-slate-500">ACCOUNT NUMBER</span>
                  <span className="font-bold text-white tracking-widest text-xs">Secure Billing Provider</span>
                </div>
              </div>
              <div className="pt-2 text-[9px] text-[#e840f3] font-bold flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5" />
                <span>SECURED CORE HANDSHAKE COMPLIANT | CloudSync-02</span>
              </div>
            </div>

            {/* WHAT-IF ROI MATRIX ENGINE */}
            <div className="bg-black/60 border border-slate-900 p-5 rounded-2xl space-y-4">
              <div className="flex items-center justify-between border-b border-slate-900 pb-2.5">
                <span className="text-[10px] text-cyan-400 font-bold tracking-widest block uppercase font-mono">WHAT-IF ROI MATRIX ENGINE</span>
                <span className="text-[8px] bg-cyan-500/10 border border-cyan-500/20 px-2 py-0.5 rounded text-cyan-400 font-bold">ACTIVE PROTOCOL</span>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between text-[9px] uppercase text-slate-400 font-bold">
                  <span>PROJECTED OPERATING HOURS</span>
                  <span className="text-cyan-400 font-bold">{roiHours} HOURS / WEEK</span>
                </div>
                <input
                  type="range"
                  id="roi-hours-slider"
                  min="0"
                  max="100"
                  value={roiHours}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    setRoiHours(val);
                    setTimeout(() => {
                      if ((window as any).NexoraxOS && (window as any).NexoraxOS.calculateDynamicROI) {
                        (window as any).NexoraxOS.calculateDynamicROI();
                      }
                    }, 10);
                  }}
                  className="w-full accent-cyan-400 h-1 bg-slate-900 rounded-lg appearance-none cursor-pointer"
                />
              </div>

              <div className="grid grid-cols-3 gap-2.5 pt-1.5 text-center font-mono">
                <div className="bg-slate-950/60 p-2 border border-slate-900/80 rounded-xl">
                  <span className="block text-[7px] text-slate-500 uppercase font-black">GROSS HARVEST</span>
                  <span id="gross-harvest-ticker" className="text-[10px] font-black text-white block mt-0.5">
                    £{(roiHours * 150 * 2.5).toLocaleString()}
                  </span>
                </div>
                <div className="bg-slate-950/60 p-2 border border-slate-900/80 rounded-xl">
                  <span className="block text-[7px] text-[#e840f3] uppercase font-black">20% NEXORAX TAX</span>
                  <span id="tax-reserve-ticker" className="text-[10px] font-black text-[#e840f3] block mt-0.5">
                    £{(roiHours * 150 * 2.5 * 0.20).toLocaleString()}
                  </span>
                </div>
                <div className="bg-slate-950/60 p-2 border border-slate-900/80 rounded-xl">
                  <span className="block text-[7px] text-emerald-400 uppercase font-black">NET finance</span>
                  <span id="net-settlement-ticker" className="text-[10px] font-black text-emerald-400 block mt-0.5">
                    £{(roiHours * 150 * 2.5 * 0.80).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Outputs */}
          <div className="bg-black border border-slate-900 rounded-2xl p-5 space-y-4 font-mono text-[10px] text-slate-400">
            <span className="text-[9px] text-emerald-400 font-bold tracking-widest block uppercase">EXTRACTION BREAKDOWN SUMMARY</span>
            
            <div className="space-y-3.5">
              <div className="flex items-center justify-between border-b border-slate-900 pb-2">
                <span className="uppercase text-slate-500">GROSS HARVEST CAPITAL:</span>
                <span className="text-xs font-black text-white">£{Number(grossHarvestInput).toLocaleString()}</span>
              </div>

              <div className="flex flex-col space-y-1 border-b border-slate-900 pb-2">
                <div className="flex items-center justify-between">
                  <span className="uppercase text-emerald-400 font-bold">1% 3% Community Allocation:</span>
                  <span className="text-xs font-bold text-emerald-400">£{levyOutput.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-[8px] text-slate-500 pt-1">
                  <div>
                    <span className="block text-[7px]">DUNFERMLINE</span>
                    <span className="text-emerald-300">£{charitySplit.c1.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                  </div>
                  <div>
                    <span className="block text-[7px]">GLOBAL FUND</span>
                    <span className="text-emerald-300">£{charitySplit.c2.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                  </div>
                  <div>
                    <span className="block text-[7px]">UK FOUNDATION</span>
                    <span className="text-emerald-300">£{charitySplit.c3.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between border-b border-slate-900 pb-2">
                <span className="uppercase text-slate-500">10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee):</span>
                <span className="text-xs font-bold text-rose-400">£{taxOutput.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
              </div>

              <div className="flex items-center justify-between bg-emerald-950/10 p-3 rounded-lg border border-emerald-500/20">
                <span className="uppercase font-extrabold text-emerald-400">NET SecurePayment SETTLEMENT ROUTED:</span>
                <span className="text-sm font-black text-white drop-shadow-[0_0_10px_rgba(16,185,129,0.3)]">£{netSettlementOutput.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* FREQUENCY SWEOPER AND TECHNICAL SIGNAL SCANNER */}
      <div id="frequency-sweeper" className="grid grid-cols-1 md:grid-cols-12 gap-8 items-stretch font-mono">
        <div className="md:col-span-4 bg-black/40 border border-slate-900 p-6 rounded-3xl flex flex-col justify-between space-y-4">
          <div className="space-y-2">
            <div className="text-cyan-400 text-[10px] font-bold tracking-widest uppercase">VOICE SIGNAL INTERCEPTOR</div>
            <h4 className="text-white text-md font-black uppercase">08000 622827 LINE TESTER</h4>
            <p className="text-[10px] text-slate-400 uppercase leading-relaxed">
              Run absolute physical frequency sweeps on the direct freephone voice channel. Validates signal-to-noise ratio and confirms secure 0.8s transmission loops with Pakistan and SecurePayment endpoints.
            </p>
          </div>

          <button
            onClick={runVoiceFrequencySweep}
            disabled={isSweeping}
            className="w-full py-2.5 bg-cyan-500/15 border border-cyan-500/30 text-cyan-400 text-xs font-black uppercase rounded-lg hover:bg-cyan-500 hover:text-black transition flex items-center justify-center gap-2 cursor-pointer"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isSweeping ? 'animate-spin' : ''}`} />
            <span>{isSweeping ? 'SWEEPING TUNNELS...' : 'ENGAGE FREQUENCY SWEEP'}</span>
          </button>
        </div>

        <div className="md:col-span-8 bg-black/85 border border-slate-950 p-6 rounded-3xl flex flex-col justify-between space-y-3 min-h-[220px]">
          <div className="flex items-center justify-between border-b border-slate-900 pb-2">
            <span className="text-[8px] text-slate-500 font-bold uppercase">SWEEP LOGS TELEMETRY DECK</span>
            {isSweeping && (
              <span className="text-[8px] text-[#e840f3] font-bold animate-pulse">SWEEPING: {sweepProgress}%</span>
            )}
          </div>
          <div className="bg-[#01040a] border border-slate-950 rounded-xl p-4 flex-1 overflow-y-auto max-h-48 custom-scrollbar space-y-2 text-[9px] text-cyan-400/80">
            {sweepResult.length === 0 ? (
              <div className="h-full flex items-center justify-center text-slate-750 uppercase italic text-[8.5px]">
                Click sweep above to initiate live telemetry sweep audit checks.
              </div>
            ) : (
              <div className="space-y-1.5">
                {sweepResult.map((res, index) => (
                  <div key={index} className="leading-relaxed uppercase">
                    {res.startsWith('[SUCCESS]') ? (
                      <span className="text-emerald-400 font-bold">{res}</span>
                    ) : res.startsWith('[0.8s]') || res.startsWith('[0.7s]') ? (
                      <span className="text-white font-semibold pl-2">{res}</span>
                    ) : (
                      <span className="pl-2">{res}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* NEXORAX AGENT NEXORAX DEPLOYMENT PANEL - PART 1/2 MANDATE */}
      <div id="nexorax-agent-deployment" className="bg-black/60 border border-cyan-500/25 rounded-3xl p-6 md:p-8 space-y-6 shadow-[0_0_35px_rgba(0,195,255,0.08)] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-80 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none" />
        
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-slate-900 pb-5">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-cyan-400 animate-ping" />
              <span className="text-[10px] text-cyan-400 font-bold font-mono tracking-widest uppercase">
                NEXORAX CORRIDOR: NODE-09.2
              </span>
            </div>
            <h3 className="text-xl md:text-2xl font-black text-white uppercase tracking-tight flex items-center gap-2">
              <Cpu className="w-5 h-5 text-cyan-400" />
              <span>NEXORAX AGENT CONTROL CENTRE</span>
            </h3>
            <p className="font-mono text-[10px] text-slate-400 uppercase leading-relaxed">
              MANDATED SECURITY VECTORS BY DIRECTOR Lead Director. CONSTANT NAME-IDENTITY LOCK ROUTINE DEPLOYED.
            </p>
          </div>

          <div className="flex items-center gap-3 bg-slate-950 border border-slate-900 px-4 py-3 rounded-xl shrink-0 font-mono">
            <div className="text-right">
              <span className="block text-[7px] text-slate-500 uppercase font-bold">SYSTEM IDENTITY LOCK</span>
              <span className="agent-naming-node text-xs font-black text-cyan-400 tracking-wider">
                NexoraX Agent
              </span>
            </div>
            <div className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400">
              <Shield className="w-4 h-4 animate-pulse" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch font-mono">
          
          {/* LEFT: Task list */}
          <div className="lg:col-span-5 space-y-3 flex flex-col justify-between">
            <div className="space-y-2.5">
              <span className="text-[9px] text-slate-500 font-bold uppercase tracking-wider block">
                MANDATORY OPERATIONAL SYSTEM TASKS
              </span>

              {[
                {
                  id: 0,
                  name: "Autonomous Universal Lead Discovery & Scraping Pipeline",
                  desc: "Automated scraping, territorial data harvest & lead compilations to Google Drive NexoraX Resource Library."
                },
                {
                  id: 1,
                  name: "Cross-Platform Deep Outreach & Closing Optimization",
                  desc: "Strike pitch distribution linking direct Stripe Payment activation URLs across core communication loops."
                },
                {
                  id: 2,
                  name: "Secure Payment Provider & GoCardless Automated Financial Binding",
                  desc: "Securing net settlements directly to Sort Verified Payment Configuration Account Secure Billing Provider with dual-tax audits."
                },
                {
                  id: 3,
                  name: "Automated Multi-Tier Resource Provisioning",
                  desc: "Provisioning insulated sandbox workspaces and digital assets configured under direct enterprise subnodes."
                }
              ].map((task) => {
                const isCurrentlyActive = activeAgentTask === task.id;
                const isCompleted = completedAgentTasks[task.id];
                
                return (
                  <div 
                    key={task.id}
                    className={`p-3.5 border rounded-xl transition-all duration-300 relative ${
                      isCurrentlyActive 
                        ? 'bg-cyan-950/20 border-cyan-500/50 shadow-[0_0_15px_rgba(6,182,212,0.1)]' 
                        : isCompleted 
                        ? 'bg-emerald-950/5 border-emerald-500/20' 
                        : 'bg-slate-950/40 border-slate-900/80 hover:border-slate-800'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3 text-left">
                      <div className="space-y-1">
                        <span className="block text-[9px] font-bold text-white uppercase tracking-tight leading-tight">
                          {task.name}
                        </span>
                        <p className="text-[8px] text-slate-500 uppercase leading-snug">
                          {task.desc}
                        </p>
                      </div>

                      <div className="shrink-0 pt-0.5">
                        {isCompleted ? (
                          <span className="text-[7.5px] font-bold border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 rounded text-emerald-400 uppercase">
                            SUCCESS
                          </span>
                        ) : isCurrentlyActive ? (
                          <span className="text-[7.5px] font-bold border border-cyan-500/30 bg-cyan-500/10 px-1.5 py-0.5 rounded text-cyan-400 uppercase animate-pulse">
                            {agentTaskProgress}%
                          </span>
                        ) : (
                          <span className="text-[7.5px] font-bold border border-slate-800 bg-slate-900/60 px-1.5 py-0.5 rounded text-slate-500 uppercase">
                            STANDBY
                          </span>
                        )}
                      </div>
                    </div>

                    {isCurrentlyActive && (
                      <div className="mt-3 h-1 bg-slate-900 rounded-full overflow-hidden">
                        <motion.div 
                          className="h-full bg-cyan-400"
                          initial={{ width: "0%" }}
                          animate={{ width: `${agentTaskProgress}%` }}
                          transition={{ duration: 0.1 }}
                        />
                      </div>
                    )}

                    {!isCurrentlyActive && !isCompleted && (
                      <button
                        onClick={() => handleExecuteAgentTask(task.id)}
                        disabled={activeAgentTask !== null}
                        className="mt-2.5 px-3 py-1 bg-cyan-500/10 border border-cyan-500/20 hover:bg-cyan-500 hover:text-black rounded text-[8px] font-bold text-cyan-400 uppercase tracking-widest transition duration-300 cursor-pointer disabled:opacity-30 disabled:pointer-events-none"
                      >
                        ENGAGE COMMAND
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            
            <div className="pt-2 text-[8px] text-slate-500 uppercase text-left flex items-center gap-1.5">
              <Award className="w-3.5 h-3.5 text-cyan-400" />
              <span>NEXORAX DIGITAL LTD REGULATORY COMPLIANT | LEVEL-18</span>
            </div>
          </div>

          {/* RIGHT: Live log output terminal */}
          <div className="lg:col-span-7 bg-[#01040a] border border-slate-950 p-5 rounded-2.5xl flex flex-col justify-between min-h-[340px] shadow-inner relative">
            <div className="flex items-center justify-between border-b border-slate-900/85 pb-2.5">
              <span className="text-[8.5px] text-slate-500 font-bold uppercase tracking-wider">
                CORE LOGGING TERMINAL DECK: SECURE_CORE_V2
              </span>
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                <span className="text-[7.5px] text-cyan-400 uppercase font-bold">
                  SANDBOX PORT CHECKPORT: LIVE
                </span>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto max-h-[220px] custom-scrollbar space-y-2 py-3 text-[9px] text-cyan-300/90 text-left transition-all duration-300">
              {agentLogs.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-slate-700 uppercase italic text-[8px] space-y-2">
                  <Terminal className="w-5 h-5 text-slate-800" />
                  <span>Awaiting task execution signal from Director dashboard...</span>
                </div>
              ) : (
                <div className="space-y-2 family-mono">
                  {agentLogs.map((log, index) => {
                    const isSuccess = log.includes("[SUCCESS]") || log.includes("[COMPLETED]") || log.includes("[INTEGRITY]");
                    const isCommand = log.includes("[MANDATE]");
                    return (
                      <div key={index} className="leading-relaxed leading-5 uppercase tracking-wide">
                        {isCommand ? (
                          <span className="text-white font-extrabold bg-cyan-500/10 px-2 py-1 rounded border border-cyan-500/25 block">
                            {log}
                          </span>
                        ) : isSuccess ? (
                          <span className="text-emerald-400 font-black pl-2 block bg-emerald-950/10 py-1 border-l-2 border-emerald-500">
                            {log}
                          </span>
                        ) : (
                          <span className="pl-3 text-cyan-200/80 block">
                            {log}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="border-t border-slate-900/85 pt-3.5 text-left text-[8px] text-slate-500 flex items-center justify-between">
              <span>STATUS ENGINE: READY FOR DECREE</span>
              <span className="font-extrabold text-slate-400">SESSION COGNITIVE LOCK: ACTIVE</span>
            </div>
          </div>

        </div>
      </div>

      {/* CTA ONBOARD COMPONENT */}
      <div id="voice-matrix-cta" className="bg-[#030612] border border-[#e840f3]/20 rounded-3xl p-6 md:p-10 relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-8 shadow-[0_0_30px_rgba(232,64,243,0.06)]">
        <div className="absolute top-0 left-0 w-80 h-80 bg-[#e840f3]/5 rounded-full blur-3xl pointer-events-none" />
        
        <div className="space-y-4 max-w-xl text-center md:text-left">
          <div className="text-[#e840f3] text-[9px] font-bold font-mono uppercase tracking-[0.3em] flex items-center gap-1.5 justify-center md:justify-start">
            <CheckCircle2 className="w-3.5 h-3.5 animate-pulse" />
            <span>MANDATORY ONBOARD SIGNALLING PROTOCOL</span>
          </div>
          <h3 className="text-2xl md:text-3xl font-sans font-black text-white uppercase tracking-tight leading-none">
            COMMISSION YOUR SECURE FORCE NODE
          </h3>
          <p className="font-mono text-[10px] text-slate-400 uppercase leading-relaxed">
            Ready to integrate custom virtual specialists into your daily business operations or state regulatory systems? Enter credentials to signal technical reviews instantly. Standard onboarding completes under 3 hours.
          </p>
          <div className="pt-2 flex flex-col sm:flex-row items-center gap-4 text-[10px] font-mono text-slate-500 uppercase justify-center md:justify-start">
            <span>DIRECT DIAL-IN: <strong className="text-white select-all">Private Data Hidden</strong></span>
            <span className="hidden sm:inline-block">/</span>
            <span>SUPPORT frequency: <strong className="text-white select-all">support@nexoraxdigital.co.uk</strong></span>
          </div>
        </div>

        {/* Signup form */}
        <form onSubmit={handleOnboardSubmit} className="w-full max-w-sm bg-black/60 border border-slate-900 rounded-2xl p-5 space-y-4 font-mono text-xs">
          <span className="text-[8px] text-[#e840f3] font-bold block uppercase tracking-widest">SECURE REGISTER DIRECT LINK</span>
          <div className="space-y-2">
            <input
              type="text"
              required
              placeholder="COMPANY / GOVERNMENT BODY"
              value={leadName}
              onChange={(e) => setLeadName(e.target.value)}
              className="w-full bg-slate-950 border border-slate-900 rounded p-2.5 text-white placeholder-slate-700 text-[10px] uppercase"
            />
            <input
              type="email"
              required
              placeholder="DIRECTOR CORRESPONDENCE EMAIL"
              value={leadEmail}
              onChange={(e) => setLeadEmail(e.target.value)}
              className="w-full bg-slate-950 border border-slate-900 rounded p-2.5 text-white placeholder-slate-700 text-[10px]"
            />
            <input
              type="text"
              placeholder="DIRECT CONTACT TELEPHONE"
              value={leadPhone}
              onChange={(e) => setLeadPhone(e.target.value)}
              className="w-full bg-slate-950 border border-slate-900 rounded p-2.5 text-white placeholder-slate-700 text-[10px]"
            />
          </div>
          <button
            type="submit"
            className="w-full py-2.5 bg-[#e840f3] hover:bg-[#e840f3]/80 text-black font-black text-[10px] rounded uppercase tracking-widest transition cursor-pointer"
          >
            DISPATCH SECURE HANDSHAKE
          </button>
        </form>
      </div>

      {/* Global Success Banner Toast */}
      <AnimatePresence>
        {successToast && (
          <div className="fixed bottom-12 left-1/2 -translate-x-1/2 bg-black/95 backdrop-blur-md border border-cyan-400 rounded-2xl p-4 shadow-[0_0_30px_rgba(0,195,255,0.3)] z-50 flex items-center gap-3.5 max-w-xl">
            <div className="w-7 h-7 rounded-full bg-cyan-500/20 border border-cyan-400 flex items-center justify-center shrink-0">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-ping" />
            </div>
            <p className="text-cyan-50 font-mono text-[9px] font-bold uppercase tracking-wider leading-relaxed pr-2">{successToast}</p>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
}
